import React, { useCallback, useContext, useState } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { Button, Spinner } from "reactstrap";
import { ProductContext } from "../../store";
import { DownloadData } from "../../services/FetchData";
import {
	SEARCH_REPORT_EXPORT_ENDPOINT,
	mapExportSortField,
} from "../../constants/searchReportExports";
import { triggerXlsxDownload } from "../../utils/triggerXlsxDownload";

const ReportExportButton = ({
	reportKey,
	sortField = null,
	sortBy = null,
	sortOrder = "desc",
}) => {
	const [exporting, setExporting] = useState(false);
	const { currentWebsiteId, dateRange } = useContext(ProductContext);

	const runExport = useCallback(async () => {
		if (!reportKey) return;
		if (!currentWebsiteId) {
			toast.error("Website ID is missing");
			return;
		}

		const rawSort = sortBy ?? sortField;
		const mappedSort = mapExportSortField(reportKey, rawSort);

		const payload = {
			reportKey,
			websiteId: currentWebsiteId,
			startDate: format(dateRange.startDate, "yyyy-MM-dd"),
			endDate: format(dateRange.endDate, "yyyy-MM-dd"),
			sortOrder: sortOrder || "desc",
		};
		if (mappedSort) {
			payload.sortField = mappedSort;
			payload.sortBy = mappedSort;
		}

		setExporting(true);
		try {
			const data = await DownloadData("ms1", SEARCH_REPORT_EXPORT_ENDPOINT, payload);

			if (
				data &&
				typeof data === "object" &&
				!(data instanceof ArrayBuffer) &&
				data.status === "success" &&
				data.data?.delivery === "email"
			) {
				toast.success(
					"Export started. We will email you when the file is ready (link valid 24 hours).",
				);
				return;
			}

			if (data instanceof ArrayBuffer) {
				const defaultName = `${reportKey}_${payload.startDate}_${payload.endDate}.xlsx`;
				triggerXlsxDownload(data, defaultName);
				toast.success("Export downloaded.");
				return;
			}

			if (data?.status === "failure" || data?.message) {
				throw new Error(
					typeof data.message === "string"
						? data.message
						: data.message?.message || "Export failed",
				);
			}

			throw new Error("Unexpected response from export");
		} catch (error) {
			console.error("Search report export error:", error);
			toast.error(error.message || "Failed to export report. Please try again.");
		} finally {
			setExporting(false);
		}
	}, [
		reportKey,
		currentWebsiteId,
		dateRange,
		sortField,
		sortBy,
		sortOrder,
	]);

	return (
		<Button
			type="button"
			color="primary"
			className="reportExportBtn"
			disabled={exporting}
			onClick={runExport}
			aria-busy={exporting}
		>
			{exporting ? (
				<Spinner size="sm" aria-hidden="true" />
			) : (
				<i className="fa fa-download" aria-hidden="true" />
			)}
			<span>{exporting ? "Exporting…" : "Export XLSX"}</span>
		</Button>
	);
};

export default ReportExportButton;
